import {
	Button,
	Checkbox,
	dateToIsoDateOnly,
	MuiAlert,
	MuiRadioGroup,
	Paper,
	ResponsiveDrawer,
	Spin,
	strIsNullOrEmpty,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useRenderer
} from '@fox/atoms';
import { Extension, useSubscribeTopicWithHandler } from '@fox/extensions';
import { PAYMENT_SELECTOR_PREFIX, PaymentSelectorProps } from '@fox/finance';
import { FlexColumnSpaced, FlexRowSpacedRightAligned, Padded, SpaceBetweenRow } from '@fox/layout';
import * as moment from 'moment';
import { nanoid } from 'nanoid';
import React, { useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { useReceipts } from '../../../Membership/query-hooks/query-implementations';
import { FINANCIAL_DATA_FETCHED, REFETCH_FINANCIAL, REFETCH_INVOICES, REFETCH_RECEIPTS } from '../../../topics';
import { useMakePaymentMutation } from '../../mutation-hooks/mutation-implementations';
import { Receipt } from '../contracts';
import {
	convertMemberPortalCapabilityToSfEnumValue,
	convertMemberPortalToSfMethodEnumValue,
	dateTimeFormat,
	formatCurrency,
	getReceiptTotal,
	ReceiptStatusChip,
	renderDate,
	TABLE_BUTTON_STYLE
} from '../shared';
import { WebPaymentType } from '../subscriptions/renew-subscription';
export function FailedReceiptsDrawer({
	data,
	selectedRows,
	useLinkedOrganisationData,
	allowedProviders
}: {
	data: Receipt[];
	selectedRows: string[];
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<div style={{ padding: '1rem 0rem' }}>
			<Button onClick={() => setOpen(true)} disabled={!selectedRows.length}>
				Resolve Selected Payments
			</Button>
			{data?.length ? (
				<ResponsiveDrawer
					headerVariant="h5"
					placement="right"
					title="Resolve failed payments"
					width={800}
					open={open}
					destroyOnClose
					onClose={() => {
						setOpen(false);
					}}
				>
					<PayFailedReceipts
						data={data}
						selectedRows={selectedRows}
						useLinkedOrganisationData={useLinkedOrganisationData}
						allowedProviders={allowedProviders}
						closeDrawer={() => setOpen(false)}
					/>
				</ResponsiveDrawer>
			) : null}
		</div>
	);
}

export function AllFailedReceiptsDrawer({
	useLinkedOrganisationData,
	allowedProviders
}: {
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<div style={{ padding: '1rem 0rem' }}>
			<Button onClick={() => setOpen(true)}>Resolve All Payments</Button>

			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title="Resolve failed payments"
				width={800}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
				}}
			>
				<AllFailedReceipts
					closeDrawer={() => setOpen(false)}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
				/>
			</ResponsiveDrawer>
		</div>
	);
}

function AllFailedReceipts({
	closeDrawer,
	useLinkedOrganisationData,
	allowedProviders
}: {
	closeDrawer: () => void;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const { contentId, requestToken } = useContentContext();
	const { error, data, loading } = useReceipts({
		requestToken,
		contentId,
		useLinkedOrganisationData,
		allowedProviders,
		page: 1,
		pageSize: 10000,
		findFailed: true
	});
	if (data?.items?.length) {
		return (
			<PayFailedReceipts
				data={data.items}
				selectedRows={data.items.map(d => d.id)}
				useLinkedOrganisationData={useLinkedOrganisationData}
				allowedProviders={allowedProviders}
				closeDrawer={() => closeDrawer()}
			/>
		);
	}
}

export function FailedSingleReceiptDrawerMobile({
	data,
	useLinkedOrganisationData,
	allowedProviders
}: {
	data: Receipt;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<>
			<Button variant="primary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Retry payment
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Resolve failed payments"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					setOpen(false);
				}}
			>
				<PayFailedReceipts
					data={[data]}
					selectedRows={[data.id]}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
					closeDrawer={() => setOpen(false)}
				/>
			</ResponsiveDrawer>
		</>
	);
}

export function FailedSingleReceiptDrawer({
	data,
	useLinkedOrganisationData,
	allowedProviders
}: {
	data: Receipt;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<>
			<Button variant="primary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Retry payment
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title="Resolve failed payments"
				width={800}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
				}}
			>
				<PayFailedReceipts
					data={[data]}
					selectedRows={[data.id]}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
					closeDrawer={() => setOpen(false)}
				/>
			</ResponsiveDrawer>
		</>
	);
}

function PayFailedReceipts({
	data,
	selectedRows,
	useLinkedOrganisationData,
	allowedProviders,
	closeDrawer
}: {
	data: Receipt[];
	selectedRows: string[];
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
	closeDrawer: () => void;
}) {
	const [payment, setPayment] = useState<WebPaymentType>();
	const [rowsToResolve, setRowsToResolve] = useState<string[]>(selectedRows);
	const { contentId, requestToken } = useContentContext();
	const { providers, customerError, customerLoading, getCustomers } = useFinancialContext();
	const customerData = getCustomers(useLinkedOrganisationData);
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	const providersToUse = allowedProviders?.length
		? providers?.filter(p => allowedProviders.includes(p.provider.toLowerCase()))
		: providers;

	const {
		loading: paymentLoading,
		mutate: makePayment,
		error: paymentError,
		data: paymentResult
	} = useMakePaymentMutation({
		requestToken,
		topics: [REFETCH_RECEIPTS, REFETCH_FINANCIAL, REFETCH_INVOICES]
	});

	if (!data) return <></>;

	//should we total for each different currency?
	const failedPayments = data.filter(
		d => d.success !== true && d.refunded !== true && d.pending !== true && d.status !== 'Replaced' && selectedRows.includes(d.id)
	);

	const failedPaymentsToResolve = failedPayments.filter(p => rowsToResolve.includes(p.id));

	const totalAmount = failedPaymentsToResolve.reduce((prev, cur) => {
		const receiptTotal = getReceiptTotal(cur);
		prev = prev + receiptTotal;
		return prev;
	}, 0);

	if (!strIsNullOrEmpty(customerError)) return <MuiAlert severity="error">{customerError}</MuiAlert>;

	const numberOfPaymentMethods = customerData.flatMap(c => c.creditCards).length + customerData.flatMap(c => c.directDebits).length;
	const hasPaymentMethods = numberOfPaymentMethods > 0;

	if (!hasPaymentMethods)
		return (
			<MuiAlert severity="warning">
				You do not have any payment methods available, please add a payment method and try again.
			</MuiAlert>
		);

	if (!paymentError && paymentResult?.id) {
		return (
			<Padded>
				<FlexColumnSpaced>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Receipt number:</Typography>
						<Typography>{paymentResult.receiptNo}</Typography>
					</SpaceBetweenRow>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Transaction reference</Typography>
						<Typography>
							<Typography>{paymentResult.transactionRef}</Typography>
						</Typography>
					</SpaceBetweenRow>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Date: </Typography>
						<Typography>{renderDate(paymentResult.createdOn)}</Typography>
					</SpaceBetweenRow>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Status</Typography>
						<ReceiptStatusChip receipt={paymentResult} />
					</SpaceBetweenRow>
					{paymentResult?.success ? (
						<MuiAlert severity="success">Payment complete</MuiAlert>
					) : (
						<MuiAlert severity="error">Payment failed</MuiAlert>
					)}
				</FlexColumnSpaced>
			</Padded>
		);
	}

	if (!failedPayments.length) return <MuiAlert severity="info">There are no failed payments selected.</MuiAlert>;

	return (
		<Padded>
			<FlexColumnSpaced>
				{!paymentError && paymentResult?.id ? (
					<FlexColumnSpaced>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Receipt number:</Typography>
							<Typography>{paymentResult.receiptNo}</Typography>
						</SpaceBetweenRow>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Transaction reference</Typography>
							<Typography>
								<Typography>{paymentResult.transactionRef}</Typography>
							</Typography>
						</SpaceBetweenRow>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Date: </Typography>
							<Typography>{renderDate(paymentResult.createdOn)}</Typography>
						</SpaceBetweenRow>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Status</Typography>
							<ReceiptStatusChip receipt={paymentResult} />
						</SpaceBetweenRow>
					</FlexColumnSpaced>
				) : (
					<FlexColumnSpaced>
						<Typography style={{ fontWeight: 'bold' }}>
							{failedPayments.length > 1
								? 'Select which failed payments are included in this transaction:'
								: 'The following failed payment is included in this transaction:'}
						</Typography>
						{failedPayments.length > 0 ? (
							<TableContainer component={Paper}>
								<Table aria-label="failed-receipts-table">
									<TableHead>
										<TableRow>
											{failedPayments.length > 1 ? <TableCell></TableCell> : null}
											<TableCell>Payment date</TableCell>
											<TableCell align="right">Receipt number</TableCell>
											<TableCell align="right">Type</TableCell>
											<TableCell align="right">Payment total</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{failedPayments.map((receipt, index) => {
											const isChecked = rowsToResolve.includes(receipt.id);
											return (
												<TableRow
													key={`${receipt.id}_${index}`}
													sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
												>
													{failedPayments.length > 1 ? (
														<TableCell
															onClick={() => {
																if (isChecked) {
																	setRowsToResolve(prev => [...prev.filter(r => r !== receipt.id)]);
																} else {
																	setRowsToResolve(prev => [...prev, receipt.id]);
																}
															}}
														>
															<Checkbox checked={isChecked} />
														</TableCell>
													) : null}
													<TableCell style={{ maxWidth: '10rem' }}>
														{moment.utc(receipt.createdOn).local().format(dateTimeFormat)}
													</TableCell>
													<TableCell align="right">{receipt.receiptNo}</TableCell>
													<TableCell align="right">
														{Array.from(new Set(receipt.lineItems.map(l => l?.lineType?.name))).join(', ')}
													</TableCell>
													<TableCell align="right">
														{formatCurrency(getReceiptTotal(receipt), receipt?.currency)}
													</TableCell>
												</TableRow>
											);
										})}
										<TableRow>
											<TableCell style={{ fontWeight: 'bold' }}>Total</TableCell>
											<TableCell style={{ fontWeight: 'bold' }} align="right">
												{formatCurrency(totalAmount, failedPayments[0].currency)}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>
						) : null}
						<Spin spinning={customerLoading}>
							{providersToUse?.length > 0 ? (
								<>
									<Typography style={{ fontWeight: 'bold' }}>Please choose your payment method:</Typography>
									<MuiRadioGroup
										value={JSON.stringify(payment) ?? null}
										name="payment"
										onChange={(_, value) => {
											const newValue = JSON.parse(value);

											setPayment(
												newValue.creditCardId
													? {
															creditCardId: newValue.creditCardId,
															method: newValue.method,
															provider: newValue.provider
													  }
													: newValue.directDebitId
													? {
															directDebitId: newValue.directDebitId,
															method: newValue.method,
															provider: newValue.provider
													  }
													: undefined
											);
										}}
									>
										{providersToUse?.map((c, i) => {
											const provider = {
												...c,
												capabilities: c.capabilities.map(capability =>
													convertMemberPortalCapabilityToSfEnumValue(capability)
												)
											};
											return (
												<React.Fragment key={`${c.provider}${c.method}`}>
													<Extension<PaymentSelectorProps<WebPaymentType>>
														name={`${PAYMENT_SELECTOR_PREFIX}.${convertMemberPortalToSfMethodEnumValue(
															c.method
														)}`}
														props={{
															customers: customerData,
															provider,
															selectedPayment: payment,
															setSelectedPayment: selectedPayment => {
																setPayment(selectedPayment);
															},
															meta: {
																useLinkedOrganisationData: useLinkedOrganisationData
															}
														}}
													/>
												</React.Fragment>
											);
										})}
									</MuiRadioGroup>
								</>
							) : (
								<MuiAlert severity="info">No payment providers available</MuiAlert>
							)}
						</Spin>
						<Typography>
							Total amount you will be charged:{' '}
							<span style={{ fontWeight: 'bold' }}>{formatCurrency(totalAmount, failedPayments[0].currency)}</span>
						</Typography>
						<FlexRowSpacedRightAligned>
							<Button
								disabled={paymentLoading || customerLoading}
								style={{ maxWidth: '100px' }}
								onClick={() => closeDrawer()}
								variant="tertiary"
								color="error"
							>
								Cancel
							</Button>
							<Button
								disabled={!payment || customerLoading || paymentLoading || failedPaymentsToResolve.length === 0}
								loading={paymentLoading}
								onClick={async () => {
									const transactionRef = moment().format('YYYYMMDD-HHmmss-') + nanoid(5);
									const lineItems = failedPaymentsToResolve.reduce<Receipt['lineItems']>((prev, cur) => {
										prev = [...prev, ...cur.lineItems];
										return prev;
									}, []);
									const input = {
										contentId,
										currency: 'AUD',
										effectiveDate: dateToIsoDateOnly(new Date()),
										lines: lineItems.map(l => ({
											amount: l.amount,
											comment: l.comment,
											lineTypeId: l.lineType.id,
											subscriptionId: l.subscriptionId
										})),
										...payment,
										receiptNo: transactionRef,
										transactionRef: transactionRef,
										useLinkedOrganisationData,
										replaces: failedPaymentsToResolve.map(p => p.id)
									};
									const result = await makePayment(input);
								}}
								sx={{ maxWidth: '100px' }}
								variant="primary"
							>
								Pay
							</Button>
						</FlexRowSpacedRightAligned>{' '}
					</FlexColumnSpaced>
				)}
				{!strIsNullOrEmpty(paymentError) ? <MuiAlert severity="error">{paymentError}</MuiAlert> : null}
			</FlexColumnSpaced>
		</Padded>
	);
}
